/**
 * Editor controller: wires the store (commands/undo), persistence (saves +
 * crash-safe drafts), canvas view, palette/inspector DOM, pointer tools
 * (build/edit/delete, pan/zoom, selection box, drag-move), shortcuts,
 * clipboard, and playtest hand-off to the game shell.
 */
import type { LevelData, LevelObject } from '../format/types';
import { validateLevel, LevelValidationError } from '../format/validate';
import { parseLevel, stringifyLevel } from '../format/serialize';
import {
  OBJECT_DEFS,
  CATEGORIES,
  defsByCategory,
  searchDefs,
  instantiate,
  type ObjectDef,
} from '../format/registry';
import { UNITS_PER_BLOCK as B } from '../engine/physics/constants';
import {
  EditorStore,
  blankLevel,
  addObjects,
  deleteObjects,
  moveObjects,
  updateObjects,
  updateLevelFields,
} from './store';
import { LevelStorage } from './persistence';
import { EditorTutorial, tutorialCompleted, snapshotCounts } from './tutorial';
import { EditorView, type EditorCamera, type OverlayData } from './view';
import { RuntimeLevel } from '../engine/level/runtime';
import { ease } from '../engine/triggers/triggers';
import { t } from '../ui/strings';
import { isEditableTarget } from '../input/input';
import type { EasingName } from '../format/types';
import { SPEED } from '../engine/physics/constants';

export interface EditorCallbacks {
  onPlaytest(level: LevelData, useStartPos: boolean): void;
  onExit(): void;
}

type Mode = 'build' | 'edit' | 'delete';

const AUTOSAVE_MS = 15_000;

export class Editor {
  private root: HTMLElement;
  private cb: EditorCallbacks;
  private storage = new LevelStorage();
  private store: EditorStore;
  private view!: EditorView;
  private canvas!: HTMLCanvasElement;
  private camera: EditorCamera = { x: 300, y: 160, zoom: 1 };
  private mode: Mode = 'build';
  private currentDef: ObjectDef = OBJECT_DEFS[0] as ObjectDef;
  private levelId: string;
  private snap = true;
  private showGrid = true;
  private showHitboxes = false;
  private showFlow = false;
  private showBpm = false;
  /** Edit-mode selection filter ('' = all types). */
  private typeFilter = '';
  /** Recently placed defIds (most recent first). */
  private recents: string[] = [];
  private overlayCache: OverlayData | null = null;
  private ghostPos: { x: number; y: number } | null = null;
  private selectionBox: { x0: number; y0: number; x1: number; y1: number } | null = null;
  private clipboard: LevelObject[] = [];
  private autosaveTimer: number | undefined;
  private rafHandle = 0;
  private disposed = false;
  private tutorial!: EditorTutorial;
  /** Drag state. */
  private drag:
    | { kind: 'pan'; lastX: number; lastY: number }
    | { kind: 'move'; startWX: number; startWY: number; movedDX: number; movedDY: number }
    | { kind: 'box'; startWX: number; startWY: number }
    | { kind: 'paint' }
    | null = null;

  constructor(root: HTMLElement, cb: EditorCallbacks, existing?: { id: string; level: LevelData }) {
    this.root = root;
    this.cb = cb;
    this.levelId = existing?.id ?? crypto.randomUUID();
    this.store = new EditorStore(existing?.level ?? blankLevel(t('ed.untitled')));
    this.buildDom();
    this.tutorial = new EditorTutorial(this.get('#editor'), () => ({
      ...snapshotCounts(this.store.level),
      mode: this.mode,
      selectionSize: this.store.selection.size,
    }));
    this.store.onChange = () => {
      this.refreshChrome();
      this.tutorial.notify();
    };
    this.attachCanvas();
    this.attachShortcuts();
    this.refreshPalette('');
    this.refreshChrome();
    if (!tutorialCompleted()) this.tutorial.start();
    this.autosaveTimer = window.setInterval(() => void this.autosave(), AUTOSAVE_MS);
    const loop = (): void => {
      if (this.disposed) return;
      this.render();
      this.rafHandle = requestAnimationFrame(loop);
    };
    this.rafHandle = requestAnimationFrame(loop);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.rafHandle);
    window.clearInterval(this.autosaveTimer);
    window.removeEventListener('keydown', this.keyHandler);
    this.root.innerHTML = '';
  }

  get level(): LevelData {
    return this.store.level;
  }

  get id(): string {
    return this.levelId;
  }

  // --- DOM ---------------------------------------------------------------------

  private buildDom(): void {
    this.root.innerHTML = `
      <div id="editor">
        <canvas id="editor-canvas"></canvas>
        <header id="editor-topbar">
          <div class="ed-group">
            <button class="ed-btn" id="ed-file">${t('ed.file')}</button>
            <div id="ed-file-menu" class="ed-menu hidden">
              <button data-act="new">${t('ed.file.new')}</button>
              <button data-act="open">${t('ed.file.open')}</button>
              <button data-act="save">${t('ed.file.save')}</button>
              <button data-act="saveas">${t('ed.file.saveas')}</button>
              <button data-act="rename">${t('ed.file.rename')}</button>
              <button data-act="duplicate">${t('ed.file.duplicate')}</button>
              <button data-act="export">${t('ed.file.export')}</button>
              <button data-act="import">${t('ed.file.import')}</button>
              <button data-act="snapshot">${t('ed.file.snapshot')}</button>
              <button data-act="restore">${t('ed.file.restore')}</button>
              <button data-act="delete" class="danger">${t('ed.file.delete')}</button>
            </div>
            <span id="ed-name" class="ed-name"></span><span id="ed-dirty"></span>
          </div>
          <div class="ed-group">
            <button class="ed-btn" id="ed-undo" title="${t('ed.undo.title')}">↶</button>
            <button class="ed-btn" id="ed-redo" title="${t('ed.redo.title')}">↷</button>
            <button class="ed-btn ed-mode" data-mode="build" title="${t('ed.mode.build.title')}">${t('ed.mode.build')}</button>
            <button class="ed-btn ed-mode" data-mode="edit" title="${t('ed.mode.edit.title')}">${t('ed.mode.edit')}</button>
            <button class="ed-btn ed-mode" data-mode="delete" title="${t('ed.mode.delete.title')}">${t('ed.mode.delete')}</button>
            <button class="ed-btn" id="ed-grid" title="${t('ed.grid.title')}">${t('ed.grid')}</button>
            <button class="ed-btn" id="ed-snap" title="${t('ed.snap.title')}">${t('ed.snap')}</button>
            <button class="ed-btn" id="ed-hitbox" title="${t('ed.hitbox.title')}">${t('ed.hitbox')}</button>
            <button class="ed-btn" id="ed-flow" title="${t('ed.flow.title')}">${t('ed.flow')}</button>
            <button class="ed-btn" id="ed-bpm" title="${t('ed.beats.title')}">${t('ed.beats')}</button>
            <select id="ed-filter" title="${t('ed.filter.title')}">
              <option value="">${t('ed.filter.all')}</option>
              <option>block</option><option>slope</option><option>oneway</option>
              <option>spike</option><option>portal</option><option>pad</option>
              <option>orb</option><option>trigger</option><option>decoration</option>
            </select>
          </div>
          <div class="ed-group">
            <span id="ed-count" class="ed-stat"></span>
            <button class="ed-btn" id="ed-tutorial-btn">${t('ed.tutorial')}</button>
            <button class="ed-btn" id="ed-settings">${t('ed.settings')}</button>
            <button class="ed-btn primary" id="ed-play" title="${t('ed.play.title')}">${t('ed.play')}</button>
            <button class="ed-btn" id="ed-exit">${t('ed.exit')}</button>
          </div>
        </header>
        <aside id="editor-palette">
          <input id="ed-search" type="search" placeholder="${t('ed.search')}" />
          <div id="ed-cats"></div>
          <div id="ed-defs"></div>
        </aside>
        <aside id="editor-inspector"></aside>
        <div id="ed-dialog-host"></div>
      </div>
    `;
    this.get('#ed-file').addEventListener('click', () =>
      this.get('#ed-file-menu').classList.toggle('hidden'),
    );
    this.get('#ed-file-menu').addEventListener('click', (e) => {
      const act = (e.target as HTMLElement).dataset.act;
      this.get('#ed-file-menu').classList.add('hidden');
      if (act) void this.fileAction(act);
    });
    this.get('#ed-undo').addEventListener('click', () => this.store.undo());
    this.get('#ed-redo').addEventListener('click', () => this.store.redo());
    for (const btn of this.root.querySelectorAll<HTMLElement>('.ed-mode')) {
      btn.addEventListener('click', () => this.setMode(btn.dataset.mode as Mode));
    }
    this.get('#ed-grid').addEventListener('click', () => {
      this.showGrid = !this.showGrid;
      this.refreshChrome();
    });
    this.get('#ed-snap').addEventListener('click', () => {
      this.snap = !this.snap;
      this.refreshChrome();
    });
    this.get('#ed-hitbox').addEventListener('click', () => this.toggleOverlay('hitbox'));
    this.get('#ed-flow').addEventListener('click', () => this.toggleOverlay('flow'));
    this.get('#ed-bpm').addEventListener('click', () => {
      this.showBpm = !this.showBpm;
      this.get('#ed-bpm').classList.toggle('active', this.showBpm);
    });
    (this.get('#ed-filter') as HTMLSelectElement).addEventListener('change', (e) => {
      this.typeFilter = (e.target as HTMLSelectElement).value;
    });
    this.get('#ed-tutorial-btn').addEventListener('click', () => this.tutorial.start());
    this.get('#ed-settings').addEventListener('click', () => this.openSettings());
    this.get('#ed-play').addEventListener('click', () => this.playtest());
    this.get('#ed-exit').addEventListener('click', () => void this.exit());
    (this.get('#ed-search') as HTMLInputElement).addEventListener('input', (e) => {
      this.refreshPalette((e.target as HTMLInputElement).value);
    });
  }

  private get(sel: string): HTMLElement {
    const el = this.root.querySelector<HTMLElement>(sel);
    if (!el) throw new Error(`Editor element missing: ${sel}`);
    return el;
  }

  private refreshChrome(): void {
    this.overlayCache = null; // level changed; recompute overlays lazily
    this.get('#ed-name').textContent = this.store.level.meta.name;
    this.get('#ed-dirty').textContent = this.store.dirty ? ' ●' : '';
    (this.get('#ed-undo') as HTMLButtonElement).disabled = !this.store.canUndo;
    (this.get('#ed-redo') as HTMLButtonElement).disabled = !this.store.canRedo;
    this.get('#ed-count').textContent = t('ed.objects', { n: this.store.level.objects.length });
    this.get('#ed-grid').classList.toggle('active', this.showGrid);
    this.get('#ed-snap').classList.toggle('active', this.snap);
    for (const btn of this.root.querySelectorAll<HTMLElement>('.ed-mode')) {
      btn.classList.toggle('active', btn.dataset.mode === this.mode);
    }
    this.refreshInspector();
  }

  private refreshPalette(query: string): void {
    const cats = this.get('#ed-cats');
    const defsEl = this.get('#ed-defs');
    cats.innerHTML = '';
    defsEl.innerHTML = '';
    const results = query ? searchDefs(query) : null;
    const renderDefs = (defs: ObjectDef[]): void => {
      for (const def of defs) {
        const btn = document.createElement('button');
        btn.className = 'ed-def' + (def.defId === this.currentDef.defId ? ' active' : '');
        btn.textContent = def.label;
        btn.addEventListener('click', () => {
          this.currentDef = def;
          this.setMode('build');
          this.refreshPalette((this.get('#ed-search') as HTMLInputElement).value);
        });
        defsEl.appendChild(btn);
      }
    };
    if (results) {
      renderDefs(results);
      return;
    }
    const addHeader = (text: string): void => {
      const h = document.createElement('div');
      h.className = 'ed-cat';
      h.textContent = text;
      defsEl.appendChild(h);
    };
    if (this.recents.length > 0) {
      addHeader(t('ed.recent'));
      renderDefs(
        this.recents
          .map((id) => OBJECT_DEFS.find((d) => d.defId === id))
          .filter((d): d is ObjectDef => d !== undefined),
      );
    }
    for (const cat of CATEGORIES) {
      addHeader(cat);
      renderDefs(defsByCategory(cat));
    }
  }

  private refreshInspector(): void {
    const el = this.get('#editor-inspector');
    const sel = this.store.selectedObjects();
    if (sel.length === 0) {
      el.innerHTML = `<div class="ed-cat">${t('ed.inspector')}</div><p class="ed-hint">${t('ed.inspector.empty')}</p>`;
      return;
    }
    const first = sel[0] as LevelObject;
    const single = sel.length === 1;
    el.innerHTML = `
      <div class="ed-cat">${t('ed.inspector.selected', { n: sel.length })}</div>
      ${
        single
          ? `
      <label class="ed-field">${t('ed.f.x')} <input id="ins-x" type="number" step="0.5" value="${(first.x - B / 2) / B}"></label>
      <label class="ed-field">${t('ed.f.y')} <input id="ins-y" type="number" step="0.5" value="${(first.y - B / 2) / B}"></label>
      <label class="ed-field">${t('ed.f.rot')} <input id="ins-rot" type="number" step="15" value="${first.rot ?? 0}"></label>
      <label class="ed-field">${t('ed.f.z')} <input id="ins-z" type="number" step="1" value="${first.z ?? 0}"></label>
      <label class="ed-field">${t('ed.f.flipy')} <input id="ins-flipy" type="checkbox" ${first.flipY ? 'checked' : ''}></label>
      <label class="ed-field">${t('ed.f.groups')} <input id="ins-groups" type="text" placeholder="${t('ed.f.groups-ph')}" value="${(first.groups ?? []).join(',')}"></label>
      <label class="ed-field">${t('ed.f.channel')} <input id="ins-channel" type="number" min="0" step="1" value="${first.colorChannel ?? 0}"></label>
      <label class="ed-field">${t('ed.f.hue')} <input id="ins-hsvh" type="number" step="15" value="${first.hsv?.h ?? 0}"></label>
      <label class="ed-field">${t('ed.f.sat')} <input id="ins-hsvs" type="number" step="0.1" value="${first.hsv?.s ?? 0}"></label>
      <label class="ed-field">${t('ed.f.val')} <input id="ins-hsvv" type="number" step="0.1" value="${first.hsv?.v ?? 0}"></label>
      <label class="ed-field">${t('ed.f.ldm')} <input id="ins-ldm" type="checkbox" ${first.editor?.['ldm'] === true ? 'checked' : ''}></label>
      ${this.kindFields(first)}
      `
          : ''
      }
      <button class="ed-btn danger" id="ins-delete">${t('ed.delete-sel')}</button>
    `;
    this.get('#ins-delete').addEventListener('click', () => this.deleteSelection());
    if (!single) return;
    const commit = (): void => {
      const before = structuredClone(first);
      const after = structuredClone(first);
      after.x = Number((this.get('#ins-x') as HTMLInputElement).value) * B + B / 2;
      after.y = Number((this.get('#ins-y') as HTMLInputElement).value) * B + B / 2;
      const flip = (this.get('#ins-flipy') as HTMLInputElement).checked;
      if (flip) after.flipY = true;
      else delete after.flipY;
      const rot = Number((this.get('#ins-rot') as HTMLInputElement).value);
      if (rot !== 0 && Number.isFinite(rot)) after.rot = rot;
      else delete after.rot;
      const z = Number((this.get('#ins-z') as HTMLInputElement).value);
      if (z !== 0 && Number.isInteger(z)) after.z = z;
      else delete after.z;
      const hh = Number((this.get('#ins-hsvh') as HTMLInputElement).value);
      const hs = Number((this.get('#ins-hsvs') as HTMLInputElement).value);
      const hv = Number((this.get('#ins-hsvv') as HTMLInputElement).value);
      if (hh !== 0 || hs !== 0 || hv !== 0) after.hsv = { h: hh, s: hs, v: hv };
      else delete after.hsv;
      if ((this.get('#ins-ldm') as HTMLInputElement).checked) {
        after.editor = { ...after.editor, ldm: true };
      } else if (after.editor) {
        delete after.editor['ldm'];
        if (Object.keys(after.editor).length === 0) delete after.editor;
      }
      const groups = (this.get('#ins-groups') as HTMLInputElement).value
        .split(',')
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => Number.isInteger(v) && v >= 1 && v <= 9999);
      if (groups.length > 0) after.groups = groups;
      else delete after.groups;
      const channel = Number((this.get('#ins-channel') as HTMLInputElement).value);
      if (Number.isInteger(channel) && channel >= 1) after.colorChannel = channel;
      else delete after.colorChannel;
      this.applyKindFields(after);
      this.store.execute(updateObjects([before], [after]));
    };
    for (const input of el.querySelectorAll('input, select')) {
      input.addEventListener('change', commit);
    }
  }

  /** Per-type extra inspector fields. */
  private kindFields(obj: LevelObject): string {
    if (obj.type === 'portal' && obj.props?.kind === 'teleport') {
      return `
      <label class="ed-field">${t('ed.f.dx')} <input id="ins-dx" type="number" step="1" value="${obj.props.dx ?? 0}"></label>
      <label class="ed-field">${t('ed.f.dy')} <input id="ins-dy" type="number" step="1" value="${obj.props.dy ?? 0}"></label>`;
    }
    if (obj.type === 'slope') {
      return `
      <label class="ed-field">${t('ed.f.direction')}
        <select id="ins-dir"><option value="up" ${obj.props?.dir === 'up' ? 'selected' : ''}>up</option>
        <option value="down" ${obj.props?.dir === 'down' ? 'selected' : ''}>down</option></select></label>
      <label class="ed-field">${t('ed.f.grade')}
        <select id="ins-grade"><option value="1" ${obj.props?.grade === 1 ? 'selected' : ''}>45°</option>
        <option value="0.5" ${obj.props?.grade === 0.5 ? 'selected' : ''}>22.5°</option></select></label>`;
    }
    if (obj.type === 'trigger') {
      const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : 'move';
      const p = obj.props ?? {};
      const num = (k: string, d: number): number =>
        typeof p[k] === 'number' ? (p[k] as number) : d;
      const field = (id: string, label: string, value: number, step = 0.1): string =>
        `<label class="ed-field">${label} <input id="${id}" type="number" step="${step}" value="${value}"></label>`;
      let html = `<div class="ed-cat">${t('ed.trigger-title', { kind })}</div>`;
      if (
        [
          'move',
          'rotate',
          'scale',
          'alpha',
          'toggle',
          'spawn',
          'stop',
          'pause',
          'resume',
          'followy',
          'keyframe',
          'pickup',
          'item:spawn-if',
        ].includes(kind)
      ) {
        const label =
          kind.startsWith('pickup') || kind.startsWith('item:')
            ? t('ed.f.itemid')
            : t('ed.f.target');
        html += field('trg-target', label, num('target', 1), 1);
      }
      if (kind === 'pickup') html += field('trg-factor', t('ed.f.amount'), num('factor', 1), 1);
      if (kind === 'item:spawn-if') {
        html += field('trg-factor', t('ed.f.threshold'), num('factor', 1), 1);
        html += field('trg-value', t('ed.f.spawn-group'), num('value', 1), 1);
      }
      if (['color', 'pulse', 'area:tint', 'gradient'].includes(kind)) {
        if (kind === 'color' || kind === 'pulse')
          html += field('trg-channel', t('ed.f.trg-channel'), num('channel', 1), 1);
        html += `<label class="ed-field">${t('ed.f.color')} <input id="trg-hex" type="color" value="${typeof p.hex === 'string' ? p.hex : '#ffffff'}"></label>`;
      }
      if (kind === 'gradient') {
        html += `<label class="ed-field">${t('ed.f.color-b')} <input id="trg-hex2" type="color" value="${typeof p.hex2 === 'string' ? p.hex2 : '#000000'}"></label>`;
        html += field('trg-alpha', t('ed.f.alpha'), num('alpha', 0.25), 0.05);
        html += `<label class="ed-field">${t('ed.f.vertical')} <input id="trg-vertical" type="checkbox" ${p.vertical !== false ? 'checked' : ''}></label>`;
      }
      if (kind.startsWith('area:') || (kind.startsWith('enter:') && kind !== 'enter:off')) {
        html += field(
          'trg-radius',
          kind.startsWith('area:') ? t('ed.f.radius') : t('ed.f.width'),
          num('radius', 6),
          1,
        );
      }
      if (['area:move', 'enter:move'].includes(kind)) {
        html +=
          field('trg-dx', t('ed.f.dx'), num('dx', 0), 0.5) +
          field('trg-dy', t('ed.f.dy'), num('dy', 0), 0.5);
      }
      if (['area:rotate', 'enter:rotate'].includes(kind))
        html += field('trg-degrees', t('ed.f.degrees'), num('degrees', 45), 15);
      if (['area:scale', 'enter:scale'].includes(kind))
        html += field('trg-scale', t('ed.f.scale'), num('scale', 1.5), 0.1);
      if (kind === 'area:fade') html += field('trg-alpha', t('ed.f.alpha'), num('alpha', 0.2), 0.1);
      if (kind === 'keyframe') {
        html += `<label class="ed-field">${t('ed.f.path')} <input id="trg-path" type="text" placeholder="dx,dy,t;…" value="${typeof p.path === 'string' ? p.path : ''}"></label>`;
        html += `<label class="ed-field">${t('ed.f.loop')} <input id="trg-loop" type="checkbox" ${p.loop === true ? 'checked' : ''}></label>`;
      }
      if (kind === 'camera:edge') {
        html += `<label class="ed-field">${t('ed.f.side')} <select id="trg-side">${[
          'left',
          'right',
          'top',
          'bottom',
        ]
          .map((sd) => `<option ${p.side === sd ? 'selected' : ''}>${sd}</option>`)
          .join('')}</select></label>`;
      }
      if (
        kind === 'move' ||
        kind === 'camera:offset' ||
        kind === 'camera:static' ||
        kind === 'camera:edge'
      ) {
        html +=
          field('trg-dx', t('ed.f.dx'), num('dx', 0), 0.5) +
          field('trg-dy', t('ed.f.dy'), num('dy', 0), 0.5);
      }
      if (kind === 'camera:rotate')
        html += field('trg-degrees', t('ed.f.degrees'), num('degrees', 10), 5);
      if (kind.startsWith('shader:'))
        html += field('trg-strength', t('ed.f.strength'), num('strength', 1), 0.1);
      if (kind === 'rotate')
        html += field('trg-degrees', t('ed.f.degrees'), num('degrees', 90), 15);
      if (kind === 'scale') html += field('trg-scale', t('ed.f.scale'), num('scale', 1.5), 0.1);
      if (kind === 'followy') html += field('trg-factor', t('ed.f.factor'), num('factor', 1), 0.25);
      if (kind === 'alpha') html += field('trg-alpha', t('ed.f.alpha'), num('alpha', 0), 0.1);
      if (kind === 'toggle')
        html += `<label class="ed-field">${t('ed.f.visible')} <input id="trg-visible" type="checkbox" ${p.visible !== false ? 'checked' : ''}></label>`;
      if (kind === 'spawn') html += field('trg-delay', t('ed.f.delay'), num('delay', 0.5));
      if (kind === 'camera:zoom') html += field('trg-zoom', t('ed.f.zoom'), num('zoom', 1.3));
      if (kind === 'camera:shake')
        html += field('trg-strength', t('ed.f.strength'), num('strength', 8), 1);
      if (kind === 'timewarp')
        html += field('trg-factor', t('ed.f.factor'), num('factor', 0.5), 0.25);
      if (
        ![
          'toggle',
          'stop',
          'spawn',
          'timewarp',
          'pause',
          'resume',
          'camera:static',
          'camera:free',
          'camera:edge',
          'camera:edge-off',
          'gradient',
          'gradient:off',
          'keyframe',
          'enter:off',
        ].includes(kind) &&
        !kind.startsWith('area:') &&
        !kind.startsWith('enter:')
      ) {
        const easing = typeof p.easing === 'string' ? p.easing : 'linear';
        html += field('trg-duration', t('ed.f.duration'), num('duration', 1));
        html += `<label class="ed-field">${t('ed.f.easing')} <select id="trg-easing">${[
          'linear',
          'in',
          'out',
          'inout',
        ]
          .map((e) => `<option ${easing === e ? 'selected' : ''}>${e}</option>`)
          .join('')}</select></label>`;
        html += `<div class="ed-easing-preview">${easingPreviewSvg(easing)}</div>`;
      }
      html += `<label class="ed-field">${t('ed.f.spawnonly')} <input id="trg-spawnonly" type="checkbox" ${p.spawnOnly === true ? 'checked' : ''}></label>`;
      return html;
    }
    if (obj.type === 'text') {
      return `
      <label class="ed-field">${t('ed.f.text')} <input id="ins-text" type="text" maxlength="120" value="${String(obj.props?.text ?? '')}"></label>
      <label class="ed-field">${t('ed.f.size')} <input id="ins-size" type="number" min="0.25" max="6" step="0.25" value="${obj.props?.size ?? 1}"></label>`;
    }
    if (obj.type === 'collectible' && obj.props?.kind === 'key') {
      return `<label class="ed-field">${t('ed.f.itemid')} <input id="ins-itemid" type="number" min="1" step="1" value="${obj.props.itemId ?? 1}"></label>`;
    }
    if (obj.type === 'lock') {
      return `
      <label class="ed-field">${t('ed.f.itemid')} <input id="ins-itemid" type="number" min="1" step="1" value="${obj.props?.itemId ?? 1}"></label>
      <label class="ed-field">${t('ed.f.needed')} <input id="ins-needed" type="number" min="1" step="1" value="${obj.props?.needed ?? 1}"></label>`;
    }
    if (obj.type === 'startpos') {
      const forms = [
        'runner',
        'glider',
        'roller',
        'hopper',
        'streak',
        'vaulter',
        'blinker',
        'pendul',
      ];
      const speeds = ['half', 'one', 'two', 'three', 'four'];
      return `
      <label class="ed-field">${t('ed.f.form')}
        <select id="ins-form">${forms.map((f) => `<option ${obj.props?.form === f ? 'selected' : ''}>${f}</option>`).join('')}</select></label>
      <label class="ed-field">${t('ed.f.speed')}
        <select id="ins-speed">${speeds.map((sp) => `<option ${obj.props?.speed === sp ? 'selected' : ''}>${sp}</option>`).join('')}</select></label>`;
    }
    return '';
  }

  private applyKindFields(obj: LevelObject): void {
    const num = (sel: string): number => Number((this.get(sel) as HTMLInputElement).value);
    const str = (sel: string): string => (this.get(sel) as HTMLSelectElement).value;
    const maybe = (sel: string): HTMLInputElement | null =>
      this.root.querySelector<HTMLInputElement>(sel);
    if (obj.type === 'trigger') {
      const p: Record<string, number | string | boolean> = { ...obj.props };
      const setNum = (key: string, sel: string): void => {
        const el = maybe(sel);
        if (el) p[key] = Number(el.value);
      };
      setNum('target', '#trg-target');
      setNum('channel', '#trg-channel');
      setNum('dx', '#trg-dx');
      setNum('dy', '#trg-dy');
      setNum('degrees', '#trg-degrees');
      setNum('alpha', '#trg-alpha');
      setNum('delay', '#trg-delay');
      setNum('zoom', '#trg-zoom');
      setNum('strength', '#trg-strength');
      setNum('factor', '#trg-factor');
      setNum('scale', '#trg-scale');
      setNum('radius', '#trg-radius');
      setNum('value', '#trg-value');
      setNum('duration', '#trg-duration');
      const hex2 = maybe('#trg-hex2');
      if (hex2) p.hex2 = hex2.value;
      const vertical = maybe('#trg-vertical');
      if (vertical) p.vertical = vertical.checked;
      const pathEl = maybe('#trg-path');
      if (pathEl) p.path = pathEl.value;
      const loop = maybe('#trg-loop');
      if (loop) p.loop = loop.checked;
      const side = this.root.querySelector<HTMLSelectElement>('#trg-side');
      if (side) p.side = side.value;
      const hex = maybe('#trg-hex');
      if (hex) p.hex = hex.value;
      const easing = this.root.querySelector<HTMLSelectElement>('#trg-easing');
      if (easing) p.easing = easing.value;
      const visible = maybe('#trg-visible');
      if (visible) p.visible = visible.checked;
      const spawnOnly = maybe('#trg-spawnonly');
      if (spawnOnly) p.spawnOnly = spawnOnly.checked;
      obj.props = p;
      return;
    }
    if (obj.type === 'portal' && obj.props?.kind === 'teleport') {
      obj.props = { ...obj.props, dx: num('#ins-dx'), dy: num('#ins-dy') };
    } else if (obj.type === 'text') {
      const el = this.root.querySelector<HTMLInputElement>('#ins-text');
      obj.props = { ...obj.props, text: el ? el.value : 'text', size: num('#ins-size') };
    } else if (obj.type === 'collectible' && obj.props?.kind === 'key') {
      obj.props = { ...obj.props, itemId: num('#ins-itemid') };
    } else if (obj.type === 'lock') {
      obj.props = { ...obj.props, itemId: num('#ins-itemid'), needed: num('#ins-needed') };
    } else if (obj.type === 'slope') {
      obj.props = { ...obj.props, dir: str('#ins-dir'), grade: Number(str('#ins-grade')) };
    } else if (obj.type === 'startpos') {
      obj.props = { ...obj.props, form: str('#ins-form'), speed: str('#ins-speed') };
    }
  }

  // --- Canvas interaction --------------------------------------------------------

  private attachCanvas(): void {
    this.canvas = this.get('#editor-canvas') as HTMLCanvasElement;
    this.view = new EditorView(this.canvas);
    this.view.resize();
    window.addEventListener('resize', () => this.view.resize());

    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const before = this.pointerWorld(e);
      this.camera.zoom = Math.max(0.08, Math.min(6, this.camera.zoom * factor));
      const after = this.pointerWorld(e);
      this.camera.x += before.x - after.x;
      this.camera.y += before.y - after.y;
    });

    this.canvas.addEventListener('pointerdown', (e) => {
      this.canvas.setPointerCapture(e.pointerId);
      const w = this.pointerWorld(e);
      if (e.button === 1 || e.button === 2) {
        this.drag = { kind: 'pan', lastX: e.clientX, lastY: e.clientY };
        return;
      }
      if (this.mode === 'build') {
        this.placeAt(w.x, w.y);
        this.drag = { kind: 'paint' };
      } else if (this.mode === 'delete') {
        this.deleteAt(w.x, w.y);
        this.drag = { kind: 'paint' };
      } else {
        const hit = this.objectAt(w.x, w.y);
        if (hit) {
          if (e.shiftKey) {
            const next = new Set(this.store.selection);
            if (next.has(hit.id)) next.delete(hit.id);
            else next.add(hit.id);
            this.store.setSelection(next);
          } else if (!this.store.selection.has(hit.id)) {
            this.store.setSelection([hit.id]);
          }
          this.drag = { kind: 'move', startWX: w.x, startWY: w.y, movedDX: 0, movedDY: 0 };
        } else {
          if (!e.shiftKey) this.store.clearSelection();
          this.drag = { kind: 'box', startWX: w.x, startWY: w.y };
        }
      }
    });

    this.canvas.addEventListener('pointermove', (e) => {
      const w = this.pointerWorld(e);
      this.ghostPos = w;
      const d = this.drag;
      if (!d) return;
      if (d.kind === 'pan') {
        const dpr = this.canvas.width / this.canvas.getBoundingClientRect().width;
        this.camera.x -= ((e.clientX - d.lastX) * dpr) / this.camera.zoom;
        this.camera.y += ((e.clientY - d.lastY) * dpr) / this.camera.zoom;
        d.lastX = e.clientX;
        d.lastY = e.clientY;
      } else if (d.kind === 'paint') {
        if (this.mode === 'build') this.placeAt(w.x, w.y);
        else if (this.mode === 'delete') this.deleteAt(w.x, w.y);
      } else if (d.kind === 'move') {
        const step = this.snap ? B : 1;
        const dx = Math.round((w.x - d.startWX) / step) * step;
        const dy = Math.round((w.y - d.startWY) / step) * step;
        if (dx !== d.movedDX || dy !== d.movedDY) {
          // Live-move without spamming undo: apply delta directly; command on release.
          const stepDX = dx - d.movedDX;
          const stepDY = dy - d.movedDY;
          for (const o of this.store.selectedObjects()) {
            o.x += stepDX;
            o.y += stepDY;
          }
          d.movedDX = dx;
          d.movedDY = dy;
        }
      } else if (d.kind === 'box') {
        this.selectionBox = { x0: d.startWX, y0: d.startWY, x1: w.x, y1: w.y };
      }
    });

    const endDrag = (e: PointerEvent): void => {
      const d = this.drag;
      this.drag = null;
      if (!d) return;
      if (d.kind === 'move' && (d.movedDX !== 0 || d.movedDY !== 0)) {
        // Rewind the live move, then re-apply through the command bus.
        const ids = [...this.store.selection];
        for (const o of this.store.selectedObjects()) {
          o.x -= d.movedDX;
          o.y -= d.movedDY;
        }
        this.store.execute(moveObjects(ids, d.movedDX, d.movedDY));
      } else if (d.kind === 'box' && this.selectionBox) {
        const b = this.selectionBox;
        const minX = Math.min(b.x0, b.x1);
        const maxX = Math.max(b.x0, b.x1);
        const minY = Math.min(b.y0, b.y1);
        const maxY = Math.max(b.y0, b.y1);
        const hits = this.store.level.objects
          .filter(
            (o) =>
              o.x >= minX &&
              o.x <= maxX &&
              o.y >= minY &&
              o.y <= maxY &&
              (!this.typeFilter || o.type === this.typeFilter),
          )
          .map((o) => o.id);
        const next = e.shiftKey ? new Set([...this.store.selection, ...hits]) : new Set(hits);
        this.store.setSelection(next);
      }
      this.selectionBox = null;
    };
    this.canvas.addEventListener('pointerup', endDrag);
    this.canvas.addEventListener('pointercancel', endDrag);
  }

  private pointerWorld(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const c = this.view.clientToCanvas(e);
    return this.view.toWorld(this.camera, c.x, c.y);
  }

  private snapPos(wx: number, wy: number): { x: number; y: number } {
    if (!this.snap) return { x: wx, y: wy };
    return {
      x: Math.floor(wx / B) * B + B / 2,
      y: Math.floor(wy / B) * B + B / 2,
    };
  }

  private objectAt(wx: number, wy: number): LevelObject | null {
    // Topmost (last placed) first; edit-mode type filter applies.
    for (let i = this.store.level.objects.length - 1; i >= 0; i--) {
      const o = this.store.level.objects[i] as LevelObject;
      if (this.mode === 'edit' && this.typeFilter && o.type !== this.typeFilter) continue;
      if (Math.abs(wx - o.x) <= B / 2 && Math.abs(wy - o.y) <= B / 2) return o;
    }
    return null;
  }

  private placeAt(wx: number, wy: number): void {
    const p = this.snapPos(wx, wy);
    // Don't stack identical objects in the same cell while painting.
    const existing = this.objectAt(p.x, p.y);
    if (
      existing &&
      existing.x === p.x &&
      existing.y === p.y &&
      existing.type === this.currentDef.type
    )
      return;
    // Singletons: placing a new endgate/startpos replaces the old one.
    if (this.currentDef.type === 'endgate' || this.currentDef.type === 'startpos') {
      const olds = this.store.level.objects.filter((o) => o.type === this.currentDef.type);
      if (olds.length > 0) this.store.execute(deleteObjects(olds));
    }
    const obj = instantiate(this.currentDef, this.store.allocateId(), p.x, p.y);
    this.store.execute(addObjects([obj]));
    // Track recents for the palette (most recent first, deduped, capped).
    this.recents = [
      this.currentDef.defId,
      ...this.recents.filter((d) => d !== this.currentDef.defId),
    ].slice(0, 8);
  }

  private deleteAt(wx: number, wy: number): void {
    const hit = this.objectAt(wx, wy);
    if (hit) this.store.execute(deleteObjects([hit]));
  }

  private deleteSelection(): void {
    const sel = this.store.selectedObjects();
    if (sel.length > 0) {
      this.store.execute(deleteObjects(sel));
      this.store.clearSelection();
    }
  }

  // --- Shortcuts -------------------------------------------------------------------

  private keyHandler = (e: KeyboardEvent): void => {
    if (isEditableTarget(e)) return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.code === 'KeyZ') {
      e.preventDefault();
      if (e.shiftKey) this.store.redo();
      else this.store.undo();
    } else if (ctrl && e.code === 'KeyY') {
      e.preventDefault();
      this.store.redo();
    } else if (ctrl && e.code === 'KeyS') {
      e.preventDefault();
      void this.save();
    } else if (ctrl && e.code === 'KeyC') {
      this.copySelection(false);
    } else if (ctrl && e.code === 'KeyX') {
      this.copySelection(true);
    } else if (ctrl && e.code === 'KeyV') {
      this.paste(e.shiftKey);
    } else if (ctrl && e.code === 'KeyD') {
      e.preventDefault();
      this.copySelection(false);
      this.paste(false);
    } else if (e.code === 'Delete' || e.code === 'Backspace') {
      this.deleteSelection();
    } else if (e.code === 'Digit1') {
      this.setMode('build');
    } else if (e.code === 'Digit2') {
      this.setMode('edit');
    } else if (e.code === 'Digit3') {
      this.setMode('delete');
    } else if (e.code === 'KeyG') {
      this.showGrid = !this.showGrid;
      this.refreshChrome();
    } else if (e.code === 'KeyH') {
      this.snap = !this.snap;
      this.refreshChrome();
    } else if (e.code === 'KeyB') {
      this.toggleOverlay('hitbox');
    } else if (e.code === 'KeyF') {
      this.toggleOverlay('flow');
    } else if (e.code === 'KeyQ' || e.code === 'KeyE') {
      const sel = this.store.selectedObjects();
      if (sel.length > 0) {
        const delta = e.code === 'KeyQ' ? -90 : 90;
        const before = sel.map((o) => structuredClone(o));
        const after = sel.map((o) => {
          const c = structuredClone(o);
          const r = ((((c.rot ?? 0) + delta) % 360) + 360) % 360;
          if (r !== 0) c.rot = r;
          else delete c.rot;
          return c;
        });
        this.store.execute(updateObjects(before, after));
      }
    } else if (e.code === 'Space') {
      e.preventDefault();
      this.playtest();
    } else if (e.code === 'Escape') {
      this.store.clearSelection();
    } else if (e.code.startsWith('Arrow')) {
      e.preventDefault();
      const step = e.shiftKey ? 2 : B;
      const dx = e.code === 'ArrowLeft' ? -step : e.code === 'ArrowRight' ? step : 0;
      const dy = e.code === 'ArrowDown' ? -step : e.code === 'ArrowUp' ? step : 0;
      const ids = [...this.store.selection];
      if (ids.length > 0) this.store.execute(moveObjects(ids, dx, dy));
      else {
        this.camera.x += dx * 4;
        this.camera.y += dy * 4;
      }
    }
  };

  private attachShortcuts(): void {
    window.addEventListener('keydown', this.keyHandler);
  }

  private copySelection(cut: boolean): void {
    const sel = this.store.selectedObjects();
    if (sel.length === 0) return;
    this.clipboard = sel.map((o) => structuredClone(o));
    if (cut) this.deleteSelection();
  }

  private paste(inPlace: boolean): void {
    if (this.clipboard.length === 0) return;
    const offset = inPlace ? 0 : B;
    const copies = this.clipboard.map((o) => ({
      ...structuredClone(o),
      id: this.store.allocateId(),
      x: o.x + offset,
      y: o.y + offset,
    }));
    this.store.execute(addObjects(copies));
    this.store.setSelection(copies.map((o) => o.id));
  }

  private setMode(mode: Mode): void {
    this.mode = mode;
    this.refreshChrome();
    this.tutorial.notify();
  }

  // --- Diagnostics overlays ------------------------------------------------------

  private toggleOverlay(which: 'hitbox' | 'flow'): void {
    if (which === 'hitbox') this.showHitboxes = !this.showHitboxes;
    else this.showFlow = !this.showFlow;
    this.overlayCache = null;
    this.get('#ed-hitbox').classList.toggle('active', this.showHitboxes);
    this.get('#ed-flow').classList.toggle('active', this.showFlow);
  }

  /** Build overlay data from the REAL runtime representation of the level, so
   * the hitboxes shown are exactly what the engine will collide against. */
  private buildOverlay(): OverlayData | null {
    if (!this.showHitboxes && !this.showFlow) return null;
    const out: OverlayData = { solids: [], hazards: [], zones: [], flows: [] };
    const rt = new RuntimeLevel(structuredClone(this.store.level));
    if (this.showHitboxes) {
      for (const c of rt.colliders) {
        (c.kind === 'hazard' ? out.hazards : out.solids).push({ ...c });
      }
      for (const z of rt.zones) out.zones.push({ ...z });
      for (const sl of rt.slopes) out.solids.push({ ...sl });
    }
    if (this.showFlow) {
      const FLOW_COLORS: Record<string, string> = {
        move: '#4ade80',
        rotate: '#4ade80',
        scale: '#4ade80',
        followy: '#4ade80',
        toggle: '#fb923c',
        alpha: '#fb923c',
        spawn: '#f472b6',
        stop: '#f87171',
        pause: '#f87171',
        resume: '#f87171',
        color: '#38bdf8',
        pulse: '#38bdf8',
      };
      for (const trig of rt.triggers) {
        if (trig.target <= 0) continue;
        const color = FLOW_COLORS[trig.kind] ?? '#fbbf24';
        const trigObj = this.store.level.objects.find((o) => o.id === trig.id);
        if (!trigObj) continue;
        // Spawn/pause/resume/stop point at triggers in the target group;
        // everything else points at member objects (limit for readability).
        const controlKinds = ['spawn', 'stop', 'pause', 'resume'];
        const targets = this.store.level.objects
          .filter((o) =>
            controlKinds.includes(trig.kind)
              ? o.type === 'trigger' && (o.groups ?? []).includes(trig.target)
              : o.type !== 'trigger' && (o.groups ?? []).includes(trig.target),
          )
          .slice(0, 12);
        for (const target of targets) {
          out.flows.push({ x0: trigObj.x, y0: trigObj.y, x1: target.x, y1: target.y, color });
        }
      }
    }
    return out;
  }

  // --- File operations ---------------------------------------------------------------

  private async fileAction(act: string): Promise<void> {
    switch (act) {
      case 'new': {
        if (this.store.dirty && !confirm(t('ed.confirm-new'))) return;
        this.levelId = crypto.randomUUID();
        this.store = new EditorStore(blankLevel(t('ed.untitled')));
        this.store.onChange = () => this.refreshChrome();
        this.refreshChrome();
        break;
      }
      case 'open':
        await this.openDialog();
        break;
      case 'save':
        await this.save();
        break;
      case 'saveas': {
        const name = prompt(
          t('ed.prompt-saveas'),
          t('ed.copy-suffix', { name: this.store.level.meta.name }),
        );
        if (!name) return;
        this.levelId = crypto.randomUUID();
        this.store.level.meta.name = name;
        await this.save();
        break;
      }
      case 'rename': {
        const name = prompt(t('ed.prompt-rename'), this.store.level.meta.name);
        if (!name) return;
        const before = {
          meta: this.store.level.meta,
          settings: this.store.level.settings,
          song: this.store.level.song,
        };
        const after = structuredClone(before);
        after.meta.name = name;
        this.store.execute(updateLevelFields(before, after));
        break;
      }
      case 'duplicate': {
        this.levelId = crypto.randomUUID();
        this.store.level.meta.name = `${this.store.level.meta.name} copy`;
        await this.save();
        break;
      }
      case 'export':
        this.exportLevel();
        break;
      case 'snapshot':
        await this.storage.saveSnapshot(this.levelId, this.store.level);
        alert(t('ed.snapshot-saved'));
        break;
      case 'restore': {
        const snaps = await this.storage.listSnapshots(this.levelId);
        if (snaps.length === 0) {
          alert(t('ed.no-snapshots'));
          return;
        }
        const list = snaps
          .map((sn, i) => `${i + 1}. ${new Date(sn.updatedAt).toLocaleString()}`)
          .join('\n');
        const pick = prompt(t('ed.prompt-restore', { list }));
        const target = snaps[pick ? Number(pick) - 1 : -1];
        if (!target) return;
        const level = await this.storage.loadSnapshot(target.id);
        if (!level) return;
        this.store = new EditorStore(level);
        this.store.onChange = () => this.refreshChrome();
        this.store.dirty = true;
        this.refreshChrome();
        break;
      }
      case 'import':
        this.importLevel();
        break;
      case 'delete': {
        if (!confirm(t('ed.confirm-delete', { name: this.store.level.meta.name }))) return;
        await this.storage.deleteLevel(this.levelId);
        this.cb.onExit();
        break;
      }
    }
  }

  async save(): Promise<boolean> {
    try {
      this.store.level.meta.revision += 1;
      this.store.level.meta.updatedAt = new Date().toISOString();
      validateLevel(structuredClone(this.store.level) as unknown);
      await this.storage.saveLevel(this.levelId, this.store.level);
      await this.storage.clearDraft(this.levelId);
      this.store.markSaved();
      return true;
    } catch (e) {
      this.showErrors(e);
      return false;
    }
  }

  private async autosave(): Promise<void> {
    if (!this.store.dirty) return;
    try {
      await this.storage.saveDraft(this.levelId, this.store.level);
    } catch (e) {
      console.warn('Autosave failed', e);
    }
  }

  private async openDialog(): Promise<void> {
    const levels = await this.storage.listLevels();
    if (levels.length === 0) {
      alert(t('ed.no-levels'));
      return;
    }
    const list = levels
      .map((l, i) => `${i + 1}. ${l.name} (${new Date(l.updatedAt).toLocaleString()})`)
      .join('\n');
    const pick = prompt(t('ed.prompt-open', { list }));
    const idx = pick ? Number(pick) - 1 : -1;
    const target = levels[idx];
    if (!target) return;
    await this.openLevel(target.id);
  }

  async openLevel(id: string): Promise<void> {
    const draft = await this.storage.loadDraft(id);
    const saved = await this.storage.loadLevel(id);
    let level = saved;
    if (
      draft &&
      (!saved ||
        confirm(t('ed.confirm-draft', { when: new Date(draft.updatedAt).toLocaleString() })))
    ) {
      level = draft.level;
    }
    if (!level) return;
    this.levelId = id;
    this.store = new EditorStore(level);
    this.store.onChange = () => this.refreshChrome();
    if (draft && level === draft.level) this.store.dirty = true;
    this.refreshChrome();
  }

  private exportLevel(): void {
    const text = stringifyLevel(this.store.level, true);
    const blob = new Blob([text], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.store.level.meta.name.replace(/[^\w-]+/g, '_')}.ssl.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private importLevel(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.ssl';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((text) => {
        try {
          const level = parseLevel(text);
          this.levelId = crypto.randomUUID();
          this.store = new EditorStore(level);
          this.store.onChange = () => this.refreshChrome();
          this.store.dirty = true;
          this.refreshChrome();
        } catch (e) {
          this.showErrors(e);
        }
      });
    });
    input.click();
  }

  private showErrors(e: unknown): void {
    const msgs = e instanceof LevelValidationError ? e.errors.join('\n') : String(e);
    alert(t('ed.level-problems', { errors: msgs }));
  }

  // --- Settings dialog ------------------------------------------------------------------

  private openSettings(): void {
    const s = this.store.level.settings;
    const song = this.store.level.song;
    const host = this.get('#ed-dialog-host');
    host.innerHTML = `
      <div class="ed-dialog-backdrop"><div class="ed-dialog">
        <div class="ed-cat">${t('ed.level-settings')}</div>
        <label class="ed-field">${t('ed.f.mode')}
          <select id="set-mode"><option ${s.mode === 'normal' ? 'selected' : ''}>normal</option>
          <option ${s.mode === 'platformer' ? 'selected' : ''}>platformer</option></select></label>
        <label class="ed-field">${t('ed.f.speed')}
          <select id="set-speed">${['half', 'one', 'two', 'three', 'four'].map((v) => `<option ${s.speed === v ? 'selected' : ''}>${v}</option>`).join('')}</select></label>
        <label class="ed-field">${t('ed.f.start-form')}
          <select id="set-form">${['runner', 'glider', 'roller', 'hopper', 'streak', 'vaulter', 'blinker', 'pendul'].map((v) => `<option ${s.startForm === v ? 'selected' : ''}>${v}</option>`).join('')}</select></label>
        <label class="ed-field">${t('ed.f.song')}
          <select id="set-song">${['neon-circuit', 'circuit-breaker', 'undercurrent', 'voltage'].map((v) => `<option ${song.id === v ? 'selected' : ''}>${v}</option>`).join('')}</select></label>
        <label class="ed-field">${t('ed.f.accent')} <input id="set-accent" type="color" value="${s.accentColor}"></label>
        <label class="ed-field">${t('ed.f.hazard')} <input id="set-hazard" type="color" value="${s.hazardColor}"></label>
        <label class="ed-field">${t('ed.f.background')} <input id="set-bg" type="color" value="${s.backgroundColor}"></label>
        <div class="ed-row">
          <button class="ed-btn primary" id="set-apply">${t('ed.apply')}</button>
          <button class="ed-btn" id="set-cancel">${t('ed.cancel')}</button>
        </div>
      </div></div>`;
    this.get('#set-cancel').addEventListener('click', () => (host.innerHTML = ''));
    this.get('#set-apply').addEventListener('click', () => {
      const before = {
        meta: this.store.level.meta,
        settings: this.store.level.settings,
        song: this.store.level.song,
      };
      const after = structuredClone(before);
      after.settings.mode = (this.get('#set-mode') as HTMLSelectElement).value as
        'normal' | 'platformer';
      after.settings.speed = (this.get('#set-speed') as HTMLSelectElement).value as typeof s.speed;
      after.settings.startForm = (this.get('#set-form') as HTMLSelectElement).value as NonNullable<
        typeof s.startForm
      >;
      after.song.id = (this.get('#set-song') as HTMLSelectElement).value;
      after.settings.accentColor = (this.get('#set-accent') as HTMLInputElement).value;
      after.settings.hazardColor = (this.get('#set-hazard') as HTMLInputElement).value;
      after.settings.backgroundColor = (this.get('#set-bg') as HTMLInputElement).value;
      this.store.execute(updateLevelFields(before, after));
      host.innerHTML = '';
    });
  }

  // --- Playtest ---------------------------------------------------------------------------

  private playtest(): void {
    try {
      const data = structuredClone(this.store.level);
      validateLevel(data as unknown);
      void this.autosave(); // never lose work entering playtest
      this.tutorial.playtested = true;
      this.tutorial.notify();
      this.cb.onPlaytest(
        data,
        data.objects.some((o) => o.type === 'startpos'),
      );
    } catch (e) {
      this.showErrors(e);
    }
  }

  private async exit(): Promise<void> {
    if (this.store.dirty) {
      const save = confirm(t('ed.confirm-exit'));
      if (!save) return;
      const ok = await this.save();
      if (!ok) return;
    }
    this.cb.onExit();
  }

  // --- Render loop ------------------------------------------------------------------------

  private render(): void {
    const ghost =
      this.mode === 'build' && this.ghostPos
        ? {
            obj: instantiate(
              this.currentDef,
              -1,
              this.snapPos(this.ghostPos.x, this.ghostPos.y).x,
              this.snapPos(this.ghostPos.x, this.ghostPos.y).y,
            ),
          }
        : null;
    if ((this.showHitboxes || this.showFlow) && !this.overlayCache) {
      this.overlayCache = this.buildOverlay();
    }
    // Beat guides: horizontal distance covered per beat at the level's speed.
    const song = this.store.level.song;
    const bpmSpacing = this.showBpm
      ? (SPEED[this.store.level.settings.speed] * 60) / song.bpm
      : null;
    this.view.draw(this.store.level, {
      camera: this.camera,
      selection: this.store.selection,
      ghost,
      selectionBox: this.selectionBox,
      showGrid: this.showGrid,
      overlay: this.showHitboxes || this.showFlow ? this.overlayCache : null,
      bpmSpacing,
    });
  }
}

/** Tiny inline SVG plot of an easing curve for the trigger inspector. */
function easingPreviewSvg(name: string): string {
  const pts: string[] = [];
  for (let i = 0; i <= 24; i++) {
    const frac = i / 24;
    const v = ease(name as EasingName, frac);
    pts.push(`${(frac * 76 + 2).toFixed(1)},${(30 - v * 28).toFixed(1)}`);
  }
  return `<svg width="80" height="32" viewBox="0 0 80 32" aria-label="easing curve">
    <polyline points="${pts.join(' ')}" fill="none" stroke="#22d3ee" stroke-width="1.5"/>
  </svg>`;
}
